/**
 * motivationEngine.js — Contextual motivational messages.
 *
 * Pure functions — zero React.
 * Takes app state, projection, and derived values, returns
 * number-aware insights for each page.
 *
 * Every message references the user's actual numbers.
 * No generic platitudes — concrete, future-focused, sharp.
 */

import { F, FF, P } from '../utils/format';
import { QUESTS } from '../features/quests/questConfig';

/* ─── Local alias: P() outputs "10.0%" but we sometimes want it inline ─── */
const pct = P;

/* ═══════════════════════════════════════════════════
   OVERVIEW INSIGHTS
   ═══════════════════════════════════════════════════ */
export function overviewInsights(s, proj, pF, fiAge, fiReached, coastAge) {
  const pool = [];
  const years = (s.maxAge || 40) - s.age;
  const growth = pF.nw / Math.max(1, s.nw0);

  // FI-related
  if (fiReached) {
    pool.push({
      icon: "⚡",
      text: `At your current pace, you hit financial independence at age ${fiAge} — ${fiAge - s.age} years from now.`,
    });
  }

  if (coastAge && coastAge <= s.age + 5) {
    pool.push({
      icon: "🏖️",
      text: `Coast FI by ${coastAge}. After that, your investments grow to cover retirement even if you stop saving.`,
    });
  }

  // Growth
  if (growth >= 10) {
    pool.push({
      icon: "📈",
      text: `Your plan turns ${F(s.nw0)} today into ${F(pF.nw)} — a ${Math.round(growth)}× return over ${years} years. Consistency compounds.`,
    });
  } else if (growth >= 3) {
    pool.push({
      icon: "🌱",
      text: `${F(s.nw0)} growing to ${F(pF.nw)} in ${years} years. That's ${Math.round(growth)}× — and it accelerates from here.`,
    });
  }

  // Savings rate
  const w = proj.find((d) => d.a >= s.startAge);
  if (w && w.comp > 0) {
    const sr = Math.round((w.sv / w.comp) * 100);
    if (sr >= 50) {
      pool.push({
        icon: "🔥",
        text: `${sr}% savings rate. You're keeping more than half of what you earn. That's elite.`,
      });
    } else if (sr >= 30) {
      pool.push({
        icon: "💪",
        text: `${sr}% savings rate. Every 5% bump here moves your FI date closer by years, not months.`,
      });
    }
  }

  // Streak bonus
  const streak = s.streak?.current || 0;
  if (streak >= 14) {
    pool.push({
      icon: "✨",
      text: `${streak}-day streak. You're showing up more consistently than 95% of people who download finance apps.`,
    });
  }

  // Quest progress
  const completed = Object.keys(s.questsCompleted || {}).length;
  const total = QUESTS.length;
  if (completed > 0 && completed < total) {
    pool.push({
      icon: "🗺️",
      text: `${completed}/${total} quests complete. Each one is proof that you're building something real.`,
    });
  }

  return pickInsights(pool, 2);
}

/* ═══════════════════════════════════════════════════
   CAREER INSIGHTS
   ═══════════════════════════════════════════════════ */
export function careerInsights(s, proj, pF) {
  const pool = [];
  const years = (s.maxAge || 40) - s.startAge;
  const lifetimeEarnings = proj.filter((d) => d.a >= s.startAge).reduce((a, d) => a + d.comp, 0);

  // Salary trajectory
  pool.push({
    icon: "📊",
    text: `At ${pct(s.salGr)} annual raises, your ${FF(s.salary)} base becomes ${FF(pF.base)} by age ${pF.a}. That's not luck — that's compound growth on your career.`,
  });

  // Lifetime earnings
  if (lifetimeEarnings > 0) {
    pool.push({
      icon: "💰",
      text: `You'll earn roughly ${F(lifetimeEarnings)} across your career. The question isn't how much you make — it's how much you keep.`,
    });
  }

  // Tax efficiency
  const w = proj.find((d) => d.a >= s.startAge);
  if (w && w.taxRate && w.margRate) {
    const spread = w.margRate - w.taxRate;
    if (spread >= 10) {
      pool.push({
        icon: "🎯",
        text: `Your ${w.taxRate}% effective vs ${w.margRate}% marginal gap means RRSP contributions save you ${w.margRate}¢ per dollar. That's powerful.`,
      });
    }
  }

  // Certifications
  const certCount = [s.cPMP, s.cAWS, s.cCISSP].filter(Boolean).length;
  if (certCount > 0) {
    const boost = certCount * 5;
    pool.push({
      icon: "🏅",
      text: `${certCount} certification${certCount > 1 ? "s" : ""} = +${boost}% salary boost. Each one stacks over every future raise.`,
    });
  }

  return pickInsights(pool, 2);
}

/* ═══════════════════════════════════════════════════
   BUDGET INSIGHTS
   ═══════════════════════════════════════════════════ */
export function budgetInsights(s) {
  const pool = [];
  const cats = s.cats || [];
  const tB = cats.reduce((a, x) => a + x.b, 0);
  const tA = cats.reduce((a, x) => a + x.a, 0);
  const surplus = tB - tA;
  const efMonths = (s.efTarget || 6);
  const efTarget = efMonths * tA;
  const efProg = efTarget > 0 ? s.cheq / efTarget : 0;

  // Under budget
  if (surplus > 0) {
    const annualSave = surplus * 12;
    pool.push({
      icon: "🎯",
      text: `${FF(surplus)}/mo under budget. That's ${FF(annualSave)}/yr you could redirect to investments.`,
    });
  } else if (surplus < 0) {
    pool.push({
      icon: "⚠️",
      text: `${FF(Math.abs(surplus))}/mo over budget. Even small reductions compound — ${FF(100)} less in spending could mean ${F(100 * 12 * 15)} more at retirement.`,
    });
  }

  // Emergency fund
  if (efProg >= 1) {
    pool.push({
      icon: "🏰",
      text: `Emergency fund fully stocked — ${efMonths} months of expenses covered. Unexpected costs won't derail your plan.`,
    });
  } else if (efProg >= 0.5) {
    pool.push({
      icon: "🛡️",
      text: `Emergency fund at ${Math.round(efProg * 100)}%. Every dollar here is insurance against having to sell investments at a loss.`,
    });
  }

  // Credit utilization
  if (s.ccLim > 0) {
    const util = (s.ccBal / s.ccLim) * 100;
    if (util <= 10) {
      pool.push({
        icon: "✅",
        text: `${util.toFixed(0)}% credit utilization — well below the 30% threshold. This keeps your score climbing.`,
      });
    } else if (util > 30) {
      pool.push({
        icon: "📉",
        text: `${util.toFixed(0)}% utilization is above the 30% threshold that drags on your credit score. Paying down ${FF(s.ccBal - s.ccLim * 0.3)} would fix this.`,
      });
    }
  }

  // Budget sync
  if (!s.budgetSync) {
    pool.push({
      icon: "🔗",
      text: `Budget sync is off — your projections use a fixed expense number. Turn it on to see how your actual spending habits shape your future.`,
    });
  }

  return pickInsights(pool, 2);
}

/* ═══════════════════════════════════════════════════
   INVESTMENTS INSIGHTS
   ═══════════════════════════════════════════════════ */
export function investmentInsights(s, proj, pF, blendedReturn) {
  const pool = [];
  const totalInvested = proj.reduce((a, d) => a + d.sv, 0);
  const totalGrowth = pF.liq - totalInvested;

  // Compound growth
  if (totalGrowth > 0 && totalInvested > 0) {
    pool.push({
      icon: "🧬",
      text: `You invest ${F(totalInvested)} over your plan. Compound growth adds ${F(totalGrowth)} on top — that's money making money.`,
    });
  }

  // FHSA usage
  if (pF.fhsa > 0) {
    pool.push({
      icon: "🏠",
      text: `Your FHSA grows to ${F(pF.fhsa)} — tax-deductible going in, tax-free coming out. The best account in Canada for first-time buyers.`,
    });
  }

  // TFSA
  if (pF.tfsa > 0) {
    pool.push({
      icon: "🍀",
      text: `TFSA at ${F(pF.tfsa)} by age ${pF.a}. Every dollar of growth in here is permanently tax-free. Max it early.`,
    });
  }

  // Return sensitivity
  if (blendedReturn > 0) {
    const onePercentMore = pF.liq * 0.15; // rough estimate of 1% return impact
    pool.push({
      icon: "📐",
      text: `At ${pct(blendedReturn)} blended return, even a 1% shift changes your outcome by roughly ${F(onePercentMore)}. Asset allocation matters.`,
    });
  }

  return pickInsights(pool, 2);
}

/* ═══════════════════════════════════════════════════
   PROJECTIONS INSIGHTS
   ═══════════════════════════════════════════════════ */
export function projectionInsights(s, proj, pF, fiReached, fiAge, scen) {
  const pool = [];
  const swr4 = pF.nw * 0.04;

  // SWR coverage
  const coverage = swr4 / (pF.liv || s.liv);
  if (coverage >= 2) {
    pool.push({
      icon: "🏆",
      text: `Your 4% SWR covers ${coverage.toFixed(1)}× your expenses. You're not just reaching FI — you're building a fortress around it.`,
    });
  } else if (coverage >= 1) {
    pool.push({
      icon: "🎯",
      text: `4% SWR covers ${coverage.toFixed(1)}× expenses. You're past the FI line. Every dollar from here adds margin of safety.`,
    });
  }

  // Scenario spread
  if (scen) {
    const bullNW = scen.bull[scen.bull.length - 1]?.nw || 0;
    const bearNW = scen.bear[scen.bear.length - 1]?.nw || 0;
    const spread = bullNW - bearNW;
    if (spread > 0) {
      pool.push({
        icon: "📊",
        text: `Your outcomes range from ${F(bearNW)} to ${F(bullNW)} — a ${F(spread)} spread. The variables you control (savings, expenses) narrow this gap.`,
      });
    }
  }

  // FI age
  if (fiReached) {
    const yearsToFI = fiAge - s.age;
    if (yearsToFI <= 10) {
      pool.push({
        icon: "⚡",
        text: `FI in ${yearsToFI} years. Most people don't achieve this in a lifetime. Your plan is working.`,
      });
    } else {
      pool.push({
        icon: "🛤️",
        text: `${yearsToFI} years to FI. Every raise, every saved dollar, every avoided lifestyle creep shortens this path.`,
      });
    }
  }

  // Inflation awareness
  const inflation = s.inflation || 0.025;
  const inflationDrag = pF.nw * inflation;
  pool.push({
    icon: "🌡️",
    text: `At ${pct(inflation)} inflation, your money loses ~${F(inflationDrag)} in purchasing power per year at peak. This is why you invest — to outpace it.`,
  });

  return pickInsights(pool, 2);
}


/* ═══════════════════════════════════════════════════
   UTILITY
   ═══════════════════════════════════════════════════ */

/**
 * Pick up to N insights from a pool.
 * Uses a deterministic daily rotation so the same user sees
 * different messages each day without randomness on re-renders.
 */
function pickInsights(pool, n) {
  if (pool.length === 0) return [];
  if (pool.length <= n) return pool;

  // Rotate based on day-of-year for variety without randomness
  const now = new Date();
  const dayOfYear = Math.floor(
    (now - new Date(now.getFullYear(), 0, 0)) / 86400000
  );
  const start = dayOfYear % pool.length;

  const picked = [];
  for (let i = 0; i < n; i++) {
    picked.push(pool[(start + i) % pool.length]);
  }
  return picked;
}
